/**
 * StakeholderMapView.js
 * ─────────────────────────────────────────────────────────────────────────────
 * A 360° report that maps every business interaction with one counterparty.
 *
 * Visual structure (top → bottom)
 * ────────────────────────────────
 *   Stakeholder Picker
 *   ──────────────────────────────────────── after selection ──
 *   Profile Card (name · type · contact · special instructions)
 *   Trade Pipeline Banner  ← the full trade lifecycle at a glance
 *     Offers → Orders → Shipment Requests → Shipping Instructions → Claims
 *   Stat Cards row
 *   Charts row 1  (Orders by status · Offers by approval · SI by status)
 *   Charts row 2  (Shipment volume trend · Claims P&L timeline · Samples by year)
 *   Tabs
 *     0 Offers · 1 Orders · 2 Shipment Requests · 3 Shipping Instructions
 *     4 Samples · 5 Claims
 *
 * Key design points
 * ─────────────────
 * • One HTTP call → GET /stakeholder-map-view/profile/{id} returns everything.
 * • Shipping instructions carry the claims data (debit/credit note fields on
 *   the Cost entity returned inside the SI DTO).  The Claims tab and the Claims
 *   P&L chart are therefore derived from the SI list client-side.
 * • All ApexCharts instances get destroyBeforeMount:true and a unique chartId
 *   so switching between stakeholders never leaves stale chart state.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Autocomplete,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import PersonOutlineIcon         from "@mui/icons-material/PersonOutline";
import PhoneIcon                 from "@mui/icons-material/Phone";
import EmailIcon                 from "@mui/icons-material/Email";
import KeyboardArrowDownIcon     from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon       from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon               from "@mui/icons-material/Refresh";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ArrowForwardIcon          from "@mui/icons-material/ArrowForward";
import ReceiptLongIcon           from "@mui/icons-material/ReceiptLong";
import { format } from "date-fns";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import DownloadIcon     from "@mui/icons-material/Download";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

import {
  getStakeholderMapStakeholdersApi,
  getStakeholderMapProfileApi,
} from "../../../components/api/StakeholderMapApiService";
import PieChart from "../PieChart";

/* ══════════════════════════════════════════════════════════════════════════════
   SHARED HELPERS
   ══════════════════════════════════════════════════════════════════════════════ */

const fmt2 = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/** Format a number with 2 decimal places; returns "–" for non-numeric values. */
function fmtNum(val)     { const n = Number(val); return Number.isFinite(n) ? fmt2.format(n) : "–"; }

/** Format a date as dd.MM.yyyy; returns "" for falsy input. */
function formatDate(val) {
  if (!val) return "";
  try { return format(new Date(val), "dd.MM.yyyy"); } catch { return ""; }
}

/** Format "YYYY-MM" bucket to "Jan 24" for chart axis labels. */
function fmtMonth(ym) {
  try {
    const [y, m] = ym.split("-");
    return format(new Date(+y, +m - 1, 1), "MMM yy");
  } catch { return ym; }
}

/** Stable sort – preserves the relative order of equal elements. */
function stableSort(arr, comparator) {
  return arr
    .map((el, i) => [el, i])
    .sort((a, b) => { const c = comparator(a[0], b[0]); return c !== 0 ? c : a[1] - b[1]; })
    .map(([el]) => el);
}

function descComp(a, b, key) {
  const va = a[key] ?? ""; const vb = b[key] ?? "";
  if (vb < va) return -1; if (vb > va) return 1; return 0;
}
function getComparator(order, key) {
  return order === "desc" ? (a, b) => descComp(a, b, key) : (a, b) => -descComp(a, b, key);
}

/** Maps a status string to a MUI color name. */
function statusColor(status) {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s.includes("approv") || s.includes("active") || s.includes("complet")) return "success";
  if (s.includes("pending") || s.includes("review")) return "warning";
  if (s.includes("cancel") || s.includes("reject") || s.includes("declin"))  return "error";
  if (s.includes("passive") || s.includes("inactive"))                       return "default";
  return "info";
}

/**
 * Derives individual claim rows from ShippingInstructionDto objects.
 * Each SI can carry a debit note AND/OR a credit note; each becomes its own row.
 * Logic kept consistent with ClaimsReportView.
 */
function toClaims(siList) {
  const rows = [];
  (siList || []).forEach((si) => {
    const qty   = Number(si.shipmentQuantity ?? 0);
    const total = (unit) => { const u = Number(unit); return qty > 0 ? u * qty : u; };

    if (si.debitNote != null || si.debitNoteType) {
      rows.push({ key: `${si.id}-D`, siId: si.id, direction: "DEBIT",
        claimType: si.debitNoteType, amount: si.debitNote, currency: si.debitNoteCurrency,
        amountUsd: si.debitNoteUsd, totalUsd: total(si.debitNoteUsd),
        date: si.debitNoteDate, remark: si.debitNoteRemark, si });
    }
    if (si.creditNote != null || si.creditNoteType) {
      rows.push({ key: `${si.id}-C`, siId: si.id, direction: "CREDIT",
        claimType: si.creditNoteType, amount: si.creditNote, currency: si.creditNoteCurrency,
        amountUsd: si.creditNoteUsd, totalUsd: total(si.creditNoteUsd),
        date: si.creditNoteDate, remark: si.creditNoteRemark, si });
    }
  });
  return rows;
}

/**
 * Counts occurrences of each unique value for a given field name.
 * Returns { labels, series } ready for PieChart.
 */
function countByField(items, field) {
  const counts = {};
  items.forEach((item) => { const v = item[field] || "Unknown"; counts[v] = (counts[v] || 0) + 1; });
  return { labels: Object.keys(counts), series: Object.values(counts) };
}

/**
 * Date-range gate used by the client-side filter.
 *
 * Rules:
 *   - val is null/undefined → always include (record has no date, don't hide it)
 *   - from is set and val < from → exclude
 *   - to   is set and val > to   → exclude (to is treated as end-of-day)
 *
 * @param {any}    val   – raw date value (string, Date, or null)
 * @param {string} from  – ISO date string "YYYY-MM-DD" or ""
 * @param {string} to    – ISO date string "YYYY-MM-DD" or ""
 */
function inRange(val, from, to) {
  if (!val) return true;
  const d = new Date(val);
  if (from && d < new Date(from))                 return false;
  if (to   && d > new Date(to + "T23:59:59"))     return false;
  return true;
}

/* ══════════════════════════════════════════════════════════════════════════════
   SMALL SHARED UI COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

/** KPI stat card with a left accent border. */
function StatCard({ label, value, color, sub }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 2, height: "100%", borderLeft: `4px solid ${color}` }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

/** Shows a "No data" placeholder when a chart would render empty. */
function ChartOrEmpty({ hasData, children, height = 250 }) {
  if (!hasData) return (
    <Box height={height} display="flex" alignItems="center" justifyContent="center">
      <Typography variant="body2" color="text.disabled">No data</Typography>
    </Box>
  );
  return children;
}

/**
 * Generic sortable, paginated table.
 *
 * Props
 * ─────
 *   columns      { id, label, align? }[]
 *   rows         any[]
 *   renderCell   (row, colId) => ReactNode
 *   renderDetail (row) => ReactNode   – optional expandable sub-row
 *   defaultSort  column id string
 */
function SortableTable({ columns, rows, renderCell, renderDetail, defaultSort }) {
  const [order, setOrder]         = useState("desc");
  const [orderBy, setOrderBy]     = useState(defaultSort ?? columns[0]?.id ?? "");
  const [page, setPage]           = useState(0);
  const [rpp, setRpp]             = useState(10);
  const [expandedId, setExpanded] = useState(null);

  const handleSort = (id) => {
    setOrder((p) => (orderBy === id && p === "asc" ? "desc" : "asc"));
    setOrderBy(id); setPage(0);
  };

  const sorted  = useMemo(() => stableSort(rows, getComparator(order, orderBy)), [rows, order, orderBy]);
  const visible = sorted.slice(page * rpp, page * rpp + rpp);

  return (
    <>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {renderDetail && <TableCell sx={{ width: 36 }} />}
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align ?? "left"}
                  sortDirection={orderBy === col.id ? order : false}>
                  <TableSortLabel
                    active={orderBy === col.id} direction={orderBy === col.id ? order : "asc"}
                    onClick={() => handleSort(col.id)}>
                    {col.label}
                    {orderBy === col.id && (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted desc" : "sorted asc"}
                      </Box>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (renderDetail ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.disabled">No records</Typography>
                </TableCell>
              </TableRow>
            ) : visible.map((row, i) => {
              const id = row.id ?? row.key ?? i;
              const isExpanded = expandedId === id;
              return (
                <React.Fragment key={id}>
                  <TableRow hover
                    onClick={renderDetail ? () => setExpanded(isExpanded ? null : id) : undefined}
                    sx={{ cursor: renderDetail ? "pointer" : "default" }}>
                    {renderDetail && (
                      <TableCell sx={{ py: 0.5 }}>
                        <IconButton size="small">
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" />
                                      : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align ?? "left"} sx={{ py: 0.8 }}>
                        {renderCell(row, col.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderDetail && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          {renderDetail(row)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={sorted.length} page={page}
        onPageChange={(_, p) => setPage(p)} rowsPerPage={rpp}
        onRowsPerPageChange={(e) => { setRpp(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]} />
    </>
  );
}

/** Two-column key/value grid shown inside an expandable row. */
function DetailGrid({ items }) {
  return (
    <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, m: 1 }}>
      <Grid container spacing={1}>
        {items.filter(([, v]) => v != null && v !== "").map(([label, value]) => (
          <Grid item xs={6} sm={4} md={3} key={label}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" fontWeight={500}>{value}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TRADE PIPELINE BANNER
   ══════════════════════════════════════════════════════════════════════════════
   Shows the complete trade lifecycle for this counterparty as a horizontal
   flow of stage boxes connected by arrows.  The percentage badge between
   stages shows the "conversion rate" (how many records made it to the next step).

   Stage flow:  Offers → Orders → Shipment Requests → Shipping Instructions → Claims
*/

function PipelineStage({ label, count, sub, color }) {
  return (
    <Paper elevation={2} sx={{
      p: 1.5, borderRadius: 2, minWidth: { xs: 80, sm: 100 }, textAlign: "center",
      borderTop: `4px solid ${color}`, flex: 1,
    }}>
      <Typography variant="h4" fontWeight={800} color={color} lineHeight={1.1}>{count}</Typography>
      <Typography variant="caption" fontWeight={600} display="block" mt={0.3}>{label}</Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function ConversionArrow({ from, to }) {
  const pct = from > 0 ? Math.round((to / from) * 100) : null;
  return (
    <Stack alignItems="center" spacing={0} sx={{ flexShrink: 0, px: 0.5 }}>
      <ArrowForwardIcon sx={{ color: "text.disabled", fontSize: 20 }} />
      {pct != null && (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{pct}%</Typography>
      )}
    </Stack>
  );
}

/**
 * Full-width banner placing stage boxes in a row with conversion arrows between
 * each consecutive stage.  Horizontally scrollable on small screens.
 */
function TradePipeline({ profile, claims }) {
  const theme = useTheme();

  const stages = [
    {
      label: "Offers",
      count: (profile?.customerOffers?.length ?? 0) + (profile?.vendorOffers?.length ?? 0),
      sub:   `${profile?.customerOffers?.length ?? 0} cust · ${profile?.vendorOffers?.length ?? 0} vend`,
      color: theme.palette.primary.main,
    },
    {
      label: "Orders",
      count: (profile?.customerOrders?.length ?? 0) + (profile?.vendorOrders?.length ?? 0),
      sub:   `${profile?.customerOrders?.length ?? 0} cust · ${profile?.vendorOrders?.length ?? 0} vend`,
      color: theme.palette.info.main,
    },
    {
      label: "Shipment Requests",
      count: profile?.shipmentRequests?.length ?? 0,
      sub:   null,
      color: theme.palette.warning.main,
    },
    {
      label: "Shipping Instructions",
      count: profile?.shippingInstructions?.length ?? 0,
      sub:   null,
      color: "#7b5ea7",   // purple — visually distinct for the "execution" stage
    },
    {
      label: "Claims",
      count: claims.length,
      sub:   `${claims.filter((c) => c.direction === "DEBIT").length} debit · ${claims.filter((c) => c.direction === "CREDIT").length} credit`,
      color: theme.palette.error.main,
    },
  ];

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5}>
        Trade Lifecycle — from first offer to final claim
      </Typography>
      <Stack direction="row" alignItems="center" sx={{ overflowX: "auto", pb: 0.5 }}>
        {stages.map((stage, i) => (
          <React.Fragment key={stage.label}>
            <PipelineStage {...stage} />
            {i < stages.length - 1 && (
              <ConversionArrow from={stages[i].count} to={stages[i + 1].count} />
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Paper>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   INLINE APEX CHART: SHIPMENT VOLUME TREND
   ══════════════════════════════════════════════════════════════════════════════
   Area chart showing how much was shipped to/from this stakeholder over time.
   Buckets SI shipmentQuantity by month using actualTimeOfDeparture (or
   shippingInstructionDate as fallback).
*/

function ShipmentVolumeTrendChart({ shippingInstructions, chartId }) {
  const theme = useTheme();

  const { categories, data } = useMemo(() => {
    const by = {};
    (shippingInstructions || []).forEach((si) => {
      const raw = si.actualTimeOfDeparture || si.shippingInstructionDate;
      if (!raw) return;
      const d   = new Date(raw);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      by[key]   = (by[key] || 0) + Number(si.shipmentQuantity ?? 0);
    });
    const sorted = Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
    return {
      categories: sorted.map(([k]) => k),
      data: sorted.map(([, v]) => Number(v.toFixed(2))),
    };
  }, [shippingInstructions]);

  const options = {
    chart: { type: "area", toolbar: { show: false }, id: chartId, destroyBeforeMount: true, animations: { enabled: false } },
    theme: { mode: theme.palette.mode },
    colors: ["#7b5ea7"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 100] },
    },
    xaxis: {
      categories,
      tickAmount: Math.min(categories.length, 8),
      labels: { formatter: (v) => fmtMonth(v), style: { fontSize: "11px" }, rotate: -20 },
    },
    yaxis: { labels: { formatter: (v) => fmt2.format(v), style: { fontSize: "11px" } } },
    tooltip: {
      x: { formatter: (_, { dataPointIndex }) => fmtMonth(categories[dataPointIndex] ?? "") },
      y: { formatter: (v) => `${fmt2.format(v)} kg` },
    },
    grid: { borderColor: theme.palette.divider },
  };

  return (
    <ChartOrEmpty hasData={categories.length > 0} height={250}>
      <ReactApexChart
        key={chartId + categories.join("")}
        type="area"
        options={options}
        series={[{ name: "Shipped (kg)", data }]}
        height={250}
      />
    </ChartOrEmpty>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   INLINE APEX CHART: CLAIMS P&L BY MONTH
   ══════════════════════════════════════════════════════════════════════════════
   Grouped bar chart: Debit (profit) vs Credit (loss) USD totals per month.
   Lets the user see whether this relationship is net profitable over time
   and spot months with unusual claim activity.
*/

function ClaimsPnlChart({ claims, chartId }) {
  const theme = useTheme();

  const { categories, debitData, creditData } = useMemo(() => {
    const by = {};
    (claims || []).forEach((c) => {
      if (!c.date) return;
      const d   = new Date(c.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!by[key]) by[key] = { debit: 0, credit: 0 };
      const amt = Number(c.amountUsd ?? 0);
      if (c.direction === "DEBIT") by[key].debit  += amt;
      else                         by[key].credit += amt;
    });
    const sorted = Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
    return {
      categories: sorted.map(([k]) => k),
      debitData:  sorted.map(([, v]) => Number(v.debit.toFixed(4))),
      creditData: sorted.map(([, v]) => Number(v.credit.toFixed(4))),
    };
  }, [claims]);

  const options = {
    chart: { type: "bar", toolbar: { show: false }, id: chartId, destroyBeforeMount: true, animations: { enabled: false } },
    theme: { mode: theme.palette.mode },
    colors: [theme.palette.warning.main, theme.palette.success.main],
    plotOptions: { bar: { horizontal: false, borderRadius: 3, columnWidth: "60%" } },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      labels: { formatter: (v) => fmtMonth(v), style: { fontSize: "11px" }, rotate: -20 },
    },
    yaxis: { labels: { formatter: (v) => `$${fmt4.format(v)}`, style: { fontSize: "10px" } } },
    tooltip: { y: { formatter: (v) => `$ ${fmt4.format(v)}` } },
    legend: { position: "top", fontSize: "12px" },
    grid: { borderColor: theme.palette.divider },
  };

  return (
    <ChartOrEmpty hasData={categories.length > 0} height={250}>
      <ReactApexChart
        key={chartId + categories.join("")}
        type="bar"
        options={options}
        series={[
          { name: "Profit (Debit)",  data: debitData  },
          { name: "Loss (Credit)",   data: creditData },
        ]}
        height={250}
      />
    </ChartOrEmpty>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   PROFILE CARD
   ══════════════════════════════════════════════════════════════════════════════ */

function ProfileCard({ stakeholder }) {
  const theme = useTheme();
  if (!stakeholder) return null;
  return (
    <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm="auto">
          <Avatar sx={{ width: 64, height: 64, bgcolor: theme.palette.primary.main, fontSize: 24, fontWeight: 700 }}>
            {(stakeholder.name ?? "?")[0].toUpperCase()}
          </Avatar>
        </Grid>
        <Grid item xs={12} sm>
          <Stack spacing={0.4}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h5" fontWeight={700}>{stakeholder.name}</Typography>
              <Chip label={stakeholder.type} size="small" variant="outlined" color="primary" />
              {stakeholder.status && (
                <Chip label={stakeholder.status} size="small" color={statusColor(stakeholder.status)} />
              )}
            </Stack>
            {stakeholder.code && (
              <Typography variant="body2" color="text.secondary">
                Code: <strong>{stakeholder.code}</strong>
                {stakeholder.foreignName && <>&nbsp;·&nbsp;{stakeholder.foreignName}</>}
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={0.5}>
              {stakeholder.contactPerson && <Chip icon={<PersonOutlineIcon />} label={stakeholder.contactPerson} size="small" variant="outlined" />}
              {stakeholder.telephoneOne  && <Chip icon={<PhoneIcon />}         label={stakeholder.telephoneOne}  size="small" variant="outlined" />}
              {stakeholder.telephoneTwo  && <Chip icon={<PhoneIcon />}         label={stakeholder.telephoneTwo}  size="small" variant="outlined" />}
              {stakeholder.mail          && <Chip icon={<EmailIcon />}         label={stakeholder.mail}          size="small" variant="outlined" />}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB: OFFERS
   ══════════════════════════════════════════════════════════════════════════════ */

const OFFER_COLS = [
  { id: "id",               label: "ID"       },
  { id: "offerType",        label: "Type"     },
  { id: "offerYear",        label: "Year"     },
  { id: "offerGroupNumber", label: "Offer #"  },
  { id: "itemBuyingGrade",  label: "Grade"    },
  { id: "itemOrigin",       label: "Origin"   },
  { id: "volume",           label: "Volume",  align: "right" },
  { id: "approvalStatus",   label: "Approval" },
  { id: "status",           label: "Status"   },
  { id: "offerDate",        label: "Date"     },
];

function OffersTab({ offers }) {
  const [side, setSide] = useState("ALL");
  const shown = side === "ALL" ? offers : offers.filter((o) => (o.offerType ?? "").toUpperCase() === side);
  return (
    <Box>
      <Stack direction="row" spacing={1} mb={1.5}>
        <ToggleButtonGroup value={side} exclusive size="small" onChange={(_, v) => { if (v) setSide(v); }}>
          <ToggleButton value="ALL">All ({offers.length})</ToggleButton>
          <ToggleButton value="CUSTOMER">Customer ({offers.filter((o) => o.offerType === "CUSTOMER").length})</ToggleButton>
          <ToggleButton value="VENDOR">Vendor ({offers.filter((o) => o.offerType === "VENDOR").length})</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <SortableTable columns={OFFER_COLS} rows={shown} defaultSort="id"
        renderCell={(row, col) => {
          if (col === "approvalStatus") return <Chip label={row.approvalStatus ?? "–"} size="small" color={statusColor(row.approvalStatus)} variant="outlined" />;
          if (col === "status")   return <Chip label={row.status ?? "–"} size="small" variant="outlined" />;
          if (col === "offerType") return <Chip label={row.offerType ?? "–"} size="small" variant="outlined" color={row.offerType === "CUSTOMER" ? "primary" : "secondary"} />;
          if (col === "volume")   return <Typography variant="caption">{fmtNum(row.volume)} {row.volumeUnit ?? ""}</Typography>;
          if (col === "offerDate") return <Typography variant="caption">{formatDate(row.offerDate)}</Typography>;
          return <Typography variant="caption">{row[col] ?? "–"}</Typography>;
        }}
        renderDetail={(row) => (
          <DetailGrid items={[
            ["Company",    row.company],
            ["Incoterm",   row.incoterm],
            ["Pmt Term",   row.paymentTerm],
            ["First Price",row.firstOfferPrice ? `${fmtNum(row.firstOfferPrice)} ${row.firstOfferPriceCurrency ?? ""}` : null],
            ["Final Price",row.finalOfferPrice ? `${fmtNum(row.finalOfferPrice)} ${row.finalOfferPriceCurrency ?? ""}` : null],
            ["Validity",   formatDate(row.validityDate)],
            ["Nicotine",   row.salesNicotine ? `${row.salesNicotine}%` : null],
            ["Sugar",      row.salesSugar    ? `${row.salesSugar}%`    : null],
            ["Remark",     row.remark],
          ]} />
        )}
      />
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB: ORDERS
   ══════════════════════════════════════════════════════════════════════════════ */

const ORDER_COLS = [
  { id: "id",                  label: "ID"        },
  { id: "orderType",           label: "Type"      },
  { id: "purchaseOrderNumber", label: "SAP PO #"  }, // SAP purchase-order number (Order.purchaseOrderNumber)
  { id: "salesOrderNumber",    label: "SAP SO #"  }, // SAP sales-order number    (Order.salesOrderNumber)
  { id: "volume",              label: "Volume",   align: "right" },
  { id: "totalAmount",         label: "Total",    align: "right" },
  { id: "approvalStatus",      label: "Approval"  },
  { id: "orderStatus",         label: "Status"    },
  { id: "estimatedTimeOfDeparture", label: "ETD" },
];

function OrdersTab({ orders }) {
  const [side, setSide] = useState("ALL");
  const shown = side === "ALL" ? orders : orders.filter((o) => (o.orderType ?? "").toUpperCase() === side);
  return (
    <Box>
      <Stack direction="row" spacing={1} mb={1.5}>
        <ToggleButtonGroup value={side} exclusive size="small" onChange={(_, v) => { if (v) setSide(v); }}>
          <ToggleButton value="ALL">All ({orders.length})</ToggleButton>
          <ToggleButton value="CUSTOMER">Customer ({orders.filter((o) => o.orderType === "CUSTOMER").length})</ToggleButton>
          <ToggleButton value="VENDOR">Vendor ({orders.filter((o) => o.orderType === "VENDOR").length})</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <SortableTable columns={ORDER_COLS} rows={shown} defaultSort="id"
        renderCell={(row, col) => {
          if (col === "approvalStatus") return <Chip label={row.approvalStatus ?? "–"} size="small" color={statusColor(row.approvalStatus)} variant="outlined" />;
          if (col === "orderStatus") return <Chip label={row.orderStatus ?? "–"} size="small" variant="outlined" />;
          if (col === "orderType") return <Chip label={row.orderType ?? "–"} size="small" variant="outlined" color={row.orderType === "CUSTOMER" ? "primary" : "secondary"} />;
          if (col === "volume") return <Typography variant="caption">{fmtNum(row.volume)} {row.volumeUnit ?? ""}</Typography>;
          if (col === "totalAmount") return <Typography variant="caption">{fmtNum(row.totalAmount)} {row.totalAmountCurrency ?? ""}</Typography>;
          if (col === "estimatedTimeOfDeparture") return <Typography variant="caption">{formatDate(row.estimatedTimeOfDeparture)}</Typography>;
          return <Typography variant="caption">{row[col] ?? "–"}</Typography>;
        }}
        renderDetail={(row) => (
          <DetailGrid items={[
            ["Company",     row.company],
            ["Incoterm",    row.incoterm],
            ["Pmt Term",    row.paymentTerm],
            ["Final Price", row.finalPrice ? `${fmtNum(row.finalPrice)} ${row.finalPriceCurrency ?? ""}` : null],
            ["Total",       `${fmtNum(row.totalAmount)} ${row.totalAmountCurrency ?? ""}`],
            ["Packaging",   row.packaging],
            ["ETA",         formatDate(row.estimatedTimeOfArrival)],
            ["Port Load",   row.portOfLoading],
            ["Port Disch",  row.portOfDischarge],
            ["Remark",      row.remark],
          ]} />
        )}
      />
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB: SHIPMENT REQUESTS
   ══════════════════════════════════════════════════════════════════════════════ */

const SR_COLS = [
  { id: "id",                  label: "ID"       },
  { id: "shipmentRequestType", label: "Type"     },
  { id: "shipmentQuantity",    label: "Qty",     align: "right" },
  { id: "portOfLoading",       label: "POL"      },
  { id: "portOfDischarge",     label: "POD"      },
  { id: "approvalStatus",      label: "Approval" },
  { id: "status",              label: "Status"   },
  { id: "shipmentEstimatedTimeOfDeparture", label: "ETD" },
  { id: "purchaseOrderNumber", label: "PO #"     },
  { id: "salesOrderNumber",    label: "SO #"     },
];

function ShipmentsTab({ shipmentRequests }) {
  return (
    <SortableTable columns={SR_COLS} rows={shipmentRequests} defaultSort="id"
      renderCell={(row, col) => {
        if (col === "approvalStatus") return <Chip label={row.approvalStatus ?? "–"} size="small" color={statusColor(row.approvalStatus)} variant="outlined" />;
        if (col === "status") return <Chip label={row.status ?? "–"} size="small" variant="outlined" />;
        if (col === "shipmentQuantity") return <Typography variant="caption">{fmtNum(row.shipmentQuantity)} {row.shipmentQuantityUnit ?? ""}</Typography>;
        if (col === "shipmentEstimatedTimeOfDeparture") return <Typography variant="caption">{formatDate(row.shipmentEstimatedTimeOfDeparture)}</Typography>;
        return <Typography variant="caption">{row[col] ?? "–"}</Typography>;
      }}
      renderDetail={(row) => (
        <DetailGrid items={[
          ["Ship To",    row.shipmentRequestTo],
          ["Vendor",     row.purchaseOrderStakeholderName],
          ["Customer",   row.salesOrderStakeholderName],
          ["PO Company", row.purchaseOrderCompany],
          ["SO Company", row.salesOrderCompany],
          ["Destination",row.destination],
          ["Warehouse",  row.warehouse],
          ["Creator",    row.creator],
          ["Created",    formatDate(row.timestamp)],
        ]} />
      )}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB: SHIPPING INSTRUCTIONS
   ══════════════════════════════════════════════════════════════════════════════
   Shipping instructions represent EXECUTED shipments and are the source of all
   claims data.  Each row shows a "Notes" column that flags debit/credit notes
   with coloured chips so the user can identify claim-bearing SIs at a glance
   without switching to the Claims tab.
*/

const SI_COLS = [
  { id: "id",                            label: "ID"      },
  { id: "finalShippingInstructionNumber",label: "SI #"    },
  { id: "shipmentRequestType",           label: "SR Type" }, // type from the shipment request, not the SI document
  { id: "shipmentQuantity",              label: "Qty",    align: "right" },
  { id: "pointOfLoading",                label: "POL"    },
  { id: "pointOfUnloading",              label: "POD"    },
  { id: "status",                        label: "Status"  },
  { id: "shippingInstructionDate",       label: "SI Date" },
  { id: "purchaseOrderId",               label: "PO ID"   },
  { id: "purchaseOrderNumber",           label: "PO #"    },
  { id: "salesOrderId",                  label: "SO ID"   },
  { id: "salesOrderNumber",              label: "SO #"    },
  { id: "notes",                         label: "Notes"   }, // synthetic — debit/credit chips
];

function ShippingInstructionsTab({ shippingInstructions }) {
  return (
    <SortableTable columns={SI_COLS} rows={shippingInstructions} defaultSort="id"
      renderCell={(row, col) => {
        if (col === "status") return <Chip label={row.status ?? "–"} size="small" color={statusColor(row.status)} variant="outlined" />;
        if (col === "shipmentQuantity") return <Typography variant="caption">{fmtNum(row.shipmentQuantity)} {row.shipmentQuantityUnit ?? ""}</Typography>;
        if (col === "shippingInstructionDate") return <Typography variant="caption">{formatDate(row.shippingInstructionDate)}</Typography>;
        /* Notes: show coloured chips for any debit or credit note present on this SI */
        if (col === "notes") return (
          <Stack direction="row" spacing={0.5}>
            {(row.debitNote != null || row.debitNoteType) && (
              <Tooltip title={`Debit: ${fmtNum(row.debitNoteUsd)} USD${row.debitNoteType ? ` · ${row.debitNoteType}` : ""}`}>
                <Chip label="Debit" size="small" color="warning" icon={<ReceiptLongIcon />} />
              </Tooltip>
            )}
            {(row.creditNote != null || row.creditNoteType) && (
              <Tooltip title={`Credit: ${fmtNum(row.creditNoteUsd)} USD${row.creditNoteType ? ` · ${row.creditNoteType}` : ""}`}>
                <Chip label="Credit" size="small" color="success" icon={<ReceiptLongIcon />} />
              </Tooltip>
            )}
          </Stack>
        );
        return <Typography variant="caption">{row[col] ?? "–"}</Typography>;
      }}
      renderDetail={(row) => (
        <DetailGrid items={[
          /* Parties */
          ["Shipper",         row.shipperName],
          ["Consignee",       row.consigneeName],
          ["Notify",          row.notifyName],
          /* Shipment logistics */
          ["Vessel",          row.vesselName],
          ["BL Number",       row.billOfLadingNumber],
          ["BL Date",         formatDate(row.billOfLadingDate)],
          ["Date of Loading", formatDate(row.dateOfLoading)],
          ["ATD",             formatDate(row.actualTimeOfDeparture)],
          ["ATA",             formatDate(row.actualTimeOfArrival)],
          ["Container #",     row.containerNumber],
          ["Carton #",        row.cartonNumber],
          ["Destination",     row.destination],
          ["Description",     row.description],
          ["Shipped By",      row.shippedBy],
          /* Purchase Order */
          ["PO ID",           row.purchaseOrderId],
          ["PO #",            row.purchaseOrderNumber],
          ["PO Company",      row.purchaseOrderCompany],
          ["PO Incoterm",     row.purchaseOrderIncoterm],
          ["PO Volume",       row.purchaseOrderVolume != null ? `${fmtNum(row.purchaseOrderVolume)} ${row.purchaseOrderVolumeUnit ?? ""}` : null],
          ["PO Vendor",       row.purchaseOrderStakeholderName],
          /* Sales Order */
          ["SO ID",           row.salesOrderId],
          ["SO #",            row.salesOrderNumber],
          ["SO Company",      row.salesOrderCompany],
          ["SO Incoterm",     row.salesOrderIncoterm],
          ["SO Volume",       row.salesOrderVolume != null ? `${fmtNum(row.salesOrderVolume)} ${row.salesOrderVolumeUnit ?? ""}` : null],
          ["SO Customer",     row.salesOrderStakeholderName],
          /* Item */
          ["PO Grade",        row.purchaseOrderItemBuyingGrade],
          ["SO Grade",        row.salesOrderItemBuyingGrade],
          ["Customer Grade",  row.shipmentRequestCustomerGradeMark],
          ["PO Origin",       row.purchaseOrderItemOrigin],
          ["PO Crop",         row.purchaseOrderItemCrop],
          ["PO Nicotine",     row.purchaseOrderItemNicotine != null ? `${row.purchaseOrderItemNicotine}%` : null],
          ["PO Sugar",        row.purchaseOrderItemSugar    != null ? `${row.purchaseOrderItemSugar}%`    : null],
          /* Debit note details — only shown when a debit note exists */
          ...(row.debitNote != null ? [
            ["Debit Amount", `${fmtNum(row.debitNote)} ${row.debitNoteCurrency ?? ""}`],
            ["Debit USD",    fmt4.format(Number(row.debitNoteUsd ?? 0))],
            ["Debit Type",   row.debitNoteType],
            ["Debit Date",   formatDate(row.debitNoteDate)],
            ["Debit Remark", row.debitNoteRemark],
          ] : []),
          /* Credit note details — only shown when a credit note exists */
          ...(row.creditNote != null ? [
            ["Credit Amount",`${fmtNum(row.creditNote)} ${row.creditNoteCurrency ?? ""}`],
            ["Credit USD",   fmt4.format(Number(row.creditNoteUsd ?? 0))],
            ["Credit Type",  row.creditNoteType],
            ["Credit Date",  formatDate(row.creditNoteDate)],
            ["Credit Remark",row.creditNoteRemark],
          ] : []),
        ]} />
      )}
    />
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB: SAMPLES
   ══════════════════════════════════════════════════════════════════════════════ */

const SAMPLE_COLS = [
  { id: "id",             label: "ID"       },
  { id: "role",           label: "Role"     },
  { id: "itemBuyingGrade",label: "Grade"    },
  { id: "salesOrigin",    label: "Origin"   },
  { id: "salesCrop",      label: "Crop"     },
  { id: "deliveryDate",   label: "Delivery" },
  { id: "approved",       label: "Approved" },
  { id: "purpose",        label: "Purpose"  },
];

function SamplesTab({ sentSamples, receivedSamples }) {
  const [side, setSide] = useState("ALL");
  const all = useMemo(() => [
    ...sentSamples.map((s)     => ({ ...s, role: "Sent"     })),
    ...receivedSamples.map((s) => ({ ...s, role: "Received" })),
  ], [sentSamples, receivedSamples]);
  const shown = side === "ALL" ? all : all.filter((s) => s.role === side);

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={1.5}>
        <ToggleButtonGroup value={side} exclusive size="small" onChange={(_, v) => { if (v) setSide(v); }}>
          <ToggleButton value="ALL">All ({all.length})</ToggleButton>
          <ToggleButton value="Sent">Sent ({sentSamples.length})</ToggleButton>
          <ToggleButton value="Received">Received ({receivedSamples.length})</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <SortableTable columns={SAMPLE_COLS} rows={shown} defaultSort="id"
        renderCell={(row, col) => {
          if (col === "role")     return <Chip label={row.role} size="small" variant="outlined" color={row.role === "Sent" ? "primary" : "secondary"} />;
          if (col === "approved") return <Chip label={row.approved ?? "–"} size="small" color={statusColor(row.approved)} variant="outlined" />;
          if (col === "deliveryDate") return <Typography variant="caption">{formatDate(row.deliveryDate)}</Typography>;
          return <Typography variant="caption">{row[col] ?? "–"}</Typography>;
        }}
        renderDetail={(row) => (
          <DetailGrid items={[
            ["Sender",   row.senderName],
            ["Receiver", row.receiverName],
            ["AWB",      row.awbNumber],
            ["Courier",  row.courier],
            ["Nicotine", row.salesNicotine ? `${row.salesNicotine}%` : null],
            ["Sugar",    row.salesSugar    ? `${row.salesSugar}%`    : null],
            ["Feedback", row.customerFeedback],
            ["Remark",   row.remark],
          ]} />
        )}
      />
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB: CLAIMS
   ══════════════════════════════════════════════════════════════════════════════
   Claims are derived from the Shipping Instructions that carry debit and/or
   credit note data.  Each claim row shows the linked SI info (vessel, BL, dates)
   so the user can cross-reference without leaving the tab.
*/

const CLAIM_COLS = [
  { id: "direction",  label: "Note"        },
  { id: "claimType",  label: "Type"        },
  { id: "amount",     label: "Amount",    align: "right" },
  { id: "amountUsd",  label: "Unit USD",  align: "right" },
  { id: "totalUsd",   label: "Total USD", align: "right" },
  { id: "date",       label: "Date"       },
  { id: "siId",       label: "SI ID"      },
  { id: "remark",     label: "Remark"     },
];

function ClaimsTab({ claims }) {
  const [dir, setDir] = useState("ALL");
  const shown = dir === "ALL" ? claims : claims.filter((c) => c.direction === dir);

  /*
   * Running totals for the mini summary bar.
   * Use totalUsd (= qty × unitUsd already computed in toClaims) so the numbers
   * match the "Total USD" column in the table and represent real money moved,
   * not a per-kg unit price.
   */
  const debitUsd  = claims.filter((c) => c.direction === "DEBIT") .reduce((s, c) => s + Number(c.totalUsd ?? 0), 0);
  const creditUsd = claims.filter((c) => c.direction === "CREDIT").reduce((s, c) => s + Number(c.totalUsd ?? 0), 0);
  const netUsd    = debitUsd - creditUsd;

  return (
    <Box>
      {/* ── Mini P&L summary at the top of the claims tab ── */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
        <Box>
          <Typography variant="caption" color="text.secondary">Profit (Debit)</Typography>
          <Typography variant="h6" fontWeight={700} color="warning.main">$ {fmt4.format(debitUsd)}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">Loss (Credit)</Typography>
          <Typography variant="h6" fontWeight={700} color="success.main">$ {fmt4.format(creditUsd)}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Typography variant="caption" color="text.secondary">Net (Debit − Credit)</Typography>
          <Typography variant="h6" fontWeight={700} color={netUsd >= 0 ? "warning.dark" : "error.main"}>
            $ {fmt4.format(netUsd)}
          </Typography>
        </Box>
        <Box ml="auto !important">
          <ToggleButtonGroup value={dir} exclusive size="small" onChange={(_, v) => { if (v) setDir(v); }}>
            <ToggleButton value="ALL">All ({claims.length})</ToggleButton>
            <ToggleButton value="DEBIT"  sx={{ color: "warning.main"  }}>Debit  ({claims.filter((c) => c.direction === "DEBIT").length})</ToggleButton>
            <ToggleButton value="CREDIT" sx={{ color: "success.main" }}>Credit ({claims.filter((c) => c.direction === "CREDIT").length})</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>

      <SortableTable columns={CLAIM_COLS} rows={shown} defaultSort="date"
        renderCell={(row, col) => {
          if (col === "direction") return (
            <Chip label={row.direction === "DEBIT" ? "Debit (Profit)" : "Credit (Loss)"}
              size="small" variant="outlined"
              color={row.direction === "DEBIT" ? "warning" : "success"} />
          );
          if (col === "amount")   return <Typography variant="caption">{row.amount != null ? `${fmtNum(row.amount)} ${row.currency ?? ""}` : "–"}</Typography>;
          if (col === "amountUsd")return <Typography variant="caption" fontWeight={600} color={row.direction === "DEBIT" ? "warning.main" : "success.main"}>{row.amountUsd != null ? fmt4.format(Number(row.amountUsd)) : "–"}</Typography>;
          if (col === "totalUsd") return <Typography variant="caption" fontWeight={700}>{row.totalUsd != null ? fmt4.format(Number(row.totalUsd)) : "–"}</Typography>;
          if (col === "date")     return <Typography variant="caption">{formatDate(row.date)}</Typography>;
          return <Typography variant="caption">{row[col] ?? "–"}</Typography>;
        }}
        renderDetail={(row) => (
          <DetailGrid items={[
            /* Claim details */
            ["Claim Type",   row.claimType],
            ["Direction",    row.direction],
            ["Remark",       row.remark],
            /* Linked SI — the shipping instruction this claim came from */
            ["SI Number",    row.si?.finalShippingInstructionNumber ?? row.si?.id],
            ["SI Type",      row.si?.shippingInstructionType],
            ["SI Date",      formatDate(row.si?.shippingInstructionDate)],
            ["BL Number",    row.si?.billOfLadingNumber],
            ["ATD",          formatDate(row.si?.actualTimeOfDeparture)],
            ["ATA",          formatDate(row.si?.actualTimeOfArrival)],
            ["Port Load",    row.si?.pointOfLoading],
            ["Port Disch",   row.si?.pointOfUnloading],
            ["PO Vendor",    row.si?.purchaseOrderStakeholderName],
            ["SO Customer",  row.si?.salesOrderStakeholderName],
          ]} />
        )}
      />
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════════════ */

export default function StakeholderMapView() {
  const theme = useTheme();

  // ── state ──────────────────────────────────────────────────────────────────
  const [stakeholders, setStakeholders] = useState([]);   // picker list
  const [selected,     setSelected]     = useState(null); // chosen StakeholderDto
  const [profile,      setProfile]      = useState(null); // full raw StakeholderMapDto from API
  const [loading,      setLoading]      = useState(false);
  const [activeTab,    setActiveTab]    = useState(0);
  // date filter (ISO strings "YYYY-MM-DD" or "")
  const [dateFrom,     setDateFrom]     = useState("");
  const [dateTo,       setDateTo]       = useState("");

  // ── load picker list on mount ──────────────────────────────────────────────
  useEffect(() => {
    getStakeholderMapStakeholdersApi()
      .then((res) => setStakeholders(res.data ?? []))
      .catch(() => toast.error("Failed to load stakeholders."));
  }, []);

  // ── load profile when user picks a stakeholder ─────────────────────────────
  const loadProfile = useCallback(async (id) => {
    setLoading(true);
    setProfile(null);
    try {
      const res = await getStakeholderMapProfileApi(id);
      setProfile(res.data);
      setActiveTab(0);
    } catch {
      toast.error("Failed to load stakeholder profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = (_, value) => {
    setSelected(value);
    if (value) loadProfile(value.id); else setProfile(null);
  };

  // ── client-side date filter ────────────────────────────────────────────────
  /**
   * Applies dateFrom / dateTo to every section of the profile.
   * Each section is filtered by its most business-relevant date field:
   *   offers              → offerDate
   *   orders              → operationDate (order date; fallback timestamp)
   *   shipment requests   → timestamp (record creation date)
   *   shipping instr.     → shippingInstructionDate
   *   samples             → deliveryDate
   * When no filter is active the raw profile is returned unchanged.
   */
  const filtered = useMemo(() => {
    if (!profile) return null;
    if (!dateFrom && !dateTo) return profile;
    const ok = (val) => inRange(val, dateFrom, dateTo);
    return {
      ...profile,
      customerOffers:       (profile.customerOffers       ?? []).filter((o)  => ok(o.offerDate)),
      vendorOffers:         (profile.vendorOffers         ?? []).filter((o)  => ok(o.offerDate)),
      customerOrders:       (profile.customerOrders       ?? []).filter((o)  => ok(o.operationDate ?? o.timestamp)),
      vendorOrders:         (profile.vendorOrders         ?? []).filter((o)  => ok(o.operationDate ?? o.timestamp)),
      sentSamples:          (profile.sentSamples          ?? []).filter((s)  => ok(s.deliveryDate)),
      receivedSamples:      (profile.receivedSamples      ?? []).filter((s)  => ok(s.deliveryDate)),
      shipmentRequests:     (profile.shipmentRequests     ?? []).filter((sr) => ok(sr.timestamp)),
      shippingInstructions: (profile.shippingInstructions ?? []).filter((si) => ok(si.shippingInstructionDate)),
    };
  }, [profile, dateFrom, dateTo]);

  // ── derived aggregates (all use `filtered`, not raw `profile`) ───────────

  const allOffers  = useMemo(() => [
    ...(filtered?.customerOffers ?? []),
    ...(filtered?.vendorOffers   ?? []),
  ], [filtered]);

  const allOrders  = useMemo(() => [
    ...(filtered?.customerOrders ?? []),
    ...(filtered?.vendorOrders   ?? []),
  ], [filtered]);

  const allSamples = useMemo(() => [
    ...(filtered?.sentSamples     ?? []),
    ...(filtered?.receivedSamples ?? []),
  ], [filtered]);

  /**
   * Claims derived from filtered SIs and further filtered by note date so
   * a claim whose note date falls outside the range is also hidden.
   */
  const claims = useMemo(() => {
    const all = toClaims(filtered?.shippingInstructions ?? []);
    if (!dateFrom && !dateTo) return all;
    return all.filter((c) => inRange(c.date, dateFrom, dateTo));
  }, [filtered, dateFrom, dateTo]);

  // ── chart data (all consume `filtered`) ───────────────────────────────────

  const orderStatusChart   = useMemo(() => countByField(allOrders, "orderStatus"),    [allOrders]);
  const offerApprovalChart = useMemo(() => countByField(allOffers, "approvalStatus"), [allOffers]);
  const siStatusChart      = useMemo(
    () => countByField(filtered?.shippingInstructions ?? [], "status"),
    [filtered]
  );

  const sampleBarChart = useMemo(() => {
    const sentBy = {}; const recvBy = {};
    (filtered?.sentSamples     ?? []).forEach((s) => { const y = s.deliveryDate ? new Date(s.deliveryDate).getFullYear() : "N/A"; sentBy[y] = (sentBy[y] || 0) + 1; });
    (filtered?.receivedSamples ?? []).forEach((s) => { const y = s.deliveryDate ? new Date(s.deliveryDate).getFullYear() : "N/A"; recvBy[y] = (recvBy[y] || 0) + 1; });
    const years = [...new Set([...Object.keys(sentBy), ...Object.keys(recvBy)])].sort();
    return {
      categories: years,
      series: [
        { name: "Sent",     data: years.map((y) => sentBy[y] || 0), color: "#425078" },
        { name: "Received", data: years.map((y) => recvBy[y] || 0), color: "#d7e7fc" },
      ],
    };
  }, [filtered]);

  // ── tab labels with live filtered counts ──────────────────────────────────
  const tabs = [
    `Offers (${allOffers.length})`,
    `Orders (${allOrders.length})`,
    `Shipment Requests (${(filtered?.shipmentRequests ?? []).length})`,
    `Shipping Instructions (${(filtered?.shippingInstructions ?? []).length})`,
    `Samples (${allSamples.length})`,
    `Claims (${claims.length})`,
  ];

  // ── Excel export ──────────────────────────────────────────────────────────
  /**
   * Exports the currently filtered data to a multi-sheet Excel workbook.
   * Sheet layout:
   *   1. Summary   – stakeholder identity + counts + date filter applied
   *   2. Offers    – all filtered offers (customer + vendor)
   *   3. Orders    – all filtered orders (customer + vendor, with SAP numbers)
   *   4. Shipment Requests
   *   5. Shipping Instructions (with debit/credit note USD columns)
   *   6. Samples   – sent + received
   *   7. Claims    – one row per note with SI back-reference
   */
  const exportToExcel = useCallback(async () => {
    if (!filtered) return;
    try {
      const wb    = new ExcelJS.Workbook();
      wb.creator  = "SanIntl – Stakeholder Map";
      wb.created  = new Date();
      const sName = profile?.stakeholder?.name ?? "stakeholder";
      const range = (dateFrom || dateTo) ? ` · ${dateFrom || "…"}→${dateTo || "…"}` : "";

      /* ── shared helpers ── */
      const styleHeader = (ws, nCols) => {
        ws.views = [{ state: "frozen", ySplit: 1 }];
        const hr = ws.getRow(1);
        hr.height = 22;
        hr.font      = { bold: true, color: { argb: "FFFFFFFF" } };
        hr.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF425078" } };
        hr.alignment = { vertical: "middle", horizontal: "center" };
        ws.autoFilter = { from: "A1", to: `${String.fromCharCode(64 + nCols)}1` };
      };
      const zebraAndBorder = (ws) => ws.eachRow((row, rn) => {
        if (rn === 1) return;
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFE3E8EF" } },
            left: { style: "thin", color: { argb: "FFE3E8EF" } },
            bottom: { style: "thin", color: { argb: "FFE3E8EF" } },
            right: { style: "thin", color: { argb: "FFE3E8EF" } },
          };
        });
        if (rn % 2 === 0) row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFD" } };
      });

      /* ── 1. Summary ── */
      const sumWs = wb.addWorksheet("Summary", { views: [{ showGridLines: false }] });
      sumWs.columns = [{ width: 34 }, { width: 42 }];
      sumWs.mergeCells("A1:B1");
      Object.assign(sumWs.getCell("A1"), {
        value: `Stakeholder Map — ${sName}${range}`,
        font: { bold: true, size: 14, color: { argb: "FFFFFFFF" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF425078" } },
        alignment: { vertical: "middle", horizontal: "left" },
      });
      sumWs.getRow(1).height = 28;
      const debitTotal  = claims.filter((c) => c.direction === "DEBIT") .reduce((s, c) => s + Number(c.totalUsd ?? 0), 0);
      const creditTotal = claims.filter((c) => c.direction === "CREDIT").reduce((s, c) => s + Number(c.totalUsd ?? 0), 0);
      [
        ["Generated at",          format(new Date(), "dd.MM.yyyy HH:mm")],
        ["Stakeholder",           `${sName} (${profile?.stakeholder?.type ?? ""})`],
        ["Code",                  profile?.stakeholder?.code ?? ""],
        ["Date filter from",      dateFrom || "All"],
        ["Date filter to",        dateTo   || "All"],
        ["Customer Offers",       filtered.customerOffers?.length  ?? 0],
        ["Vendor Offers",         filtered.vendorOffers?.length    ?? 0],
        ["Customer Orders",       filtered.customerOrders?.length  ?? 0],
        ["Vendor Orders",         filtered.vendorOrders?.length    ?? 0],
        ["Shipment Requests",     filtered.shipmentRequests?.length ?? 0],
        ["Shipping Instructions", filtered.shippingInstructions?.length ?? 0],
        ["Samples Sent",          filtered.sentSamples?.length     ?? 0],
        ["Samples Received",      filtered.receivedSamples?.length ?? 0],
        ["Claims – Debit",        claims.filter((c) => c.direction === "DEBIT").length],
        ["Claims – Credit",       claims.filter((c) => c.direction === "CREDIT").length],
        ["Profit (Debit) USD",    debitTotal],
        ["Loss (Credit) USD",     creditTotal],
        ["Net USD",               debitTotal - creditTotal],
      ].forEach(([k, v], i) => {
        const row = sumWs.addRow([k, v]);
        row.getCell(1).font = { bold: true, color: { argb: "FF425078" } };
        if (typeof v === "number") row.getCell(2).numFmt = "#,##0.0000";
        if (i % 2 === 0) row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F9FC" } };
      });

      /* ── 2. Offers ── */
      const offerWs = wb.addWorksheet("Offers");
      offerWs.columns = [
        { header: "ID",          key: "id",          width: 10 },
        { header: "Type",        key: "offerType",   width: 12 },
        { header: "Year",        key: "offerYear",   width: 10 },
        { header: "Offer #",     key: "offerGroupNumber", width: 12 },
        { header: "Grade",       key: "itemBuyingGrade",  width: 18 },
        { header: "Origin",      key: "itemOrigin",  width: 14 },
        { header: "Volume",      key: "volume",      width: 14 },
        { header: "Vol Unit",    key: "volumeUnit",  width: 10 },
        { header: "Approval",    key: "approvalStatus",   width: 16 },
        { header: "Status",      key: "status",      width: 16 },
        { header: "Offer Date",  key: "offerDate",   width: 14 },
        { header: "First Price", key: "firstOfferPrice",  width: 14 },
        { header: "Final Price", key: "finalOfferPrice",  width: 14 },
        { header: "Currency",    key: "finalOfferPriceCurrency", width: 10 },
        { header: "Incoterm",    key: "incoterm",    width: 12 },
        { header: "Company",     key: "company",     width: 20 },
      ];
      styleHeader(offerWs, 16);
      allOffers.forEach((o) => offerWs.addRow({
        id: o.id, offerType: o.offerType, offerYear: o.offerYear,
        offerGroupNumber: o.offerGroupNumber, itemBuyingGrade: o.itemBuyingGrade,
        itemOrigin: o.itemOrigin,
        volume: o.volume != null ? Number(o.volume) : "", volumeUnit: o.volumeUnit,
        approvalStatus: o.approvalStatus, status: o.status,
        offerDate: formatDate(o.offerDate),
        firstOfferPrice: o.firstOfferPrice != null ? Number(o.firstOfferPrice) : "",
        finalOfferPrice: o.finalOfferPrice != null ? Number(o.finalOfferPrice) : "",
        finalOfferPriceCurrency: o.finalOfferPriceCurrency,
        incoterm: o.incoterm, company: o.company,
      }));
      zebraAndBorder(offerWs);

      /* ── 3. Orders ── */
      const orderWs = wb.addWorksheet("Orders");
      orderWs.columns = [
        { header: "ID",          key: "id",          width: 10 },
        { header: "Type",        key: "orderType",   width: 12 },
        { header: "SAP PO #",    key: "purchaseOrderNumber", width: 16 },
        { header: "SAP SO #",    key: "salesOrderNumber",    width: 16 },
        { header: "Volume",      key: "volume",      width: 14 },
        { header: "Vol Unit",    key: "volumeUnit",  width: 10 },
        { header: "Total",       key: "totalAmount", width: 16 },
        { header: "Currency",    key: "totalAmountCurrency", width: 10 },
        { header: "Approval",    key: "approvalStatus",  width: 16 },
        { header: "Status",      key: "orderStatus", width: 16 },
        { header: "ETD",         key: "etd",         width: 14 },
        { header: "Final Price", key: "finalPrice",  width: 14 },
        { header: "Incoterm",    key: "incoterm",    width: 12 },
        { header: "Company",     key: "company",     width: 20 },
      ];
      styleHeader(orderWs, 16);
      allOrders.forEach((o) => orderWs.addRow({
        id: o.id, orderType: o.orderType, orderYear: o.orderYear,
        orderGroupNumber: o.orderGroupNumber,
        purchaseOrderNumber: o.purchaseOrderNumber ?? "",
        salesOrderNumber: o.salesOrderNumber ?? "",
        volume: o.volume != null ? Number(o.volume) : "", volumeUnit: o.volumeUnit,
        totalAmount: o.totalAmount != null ? Number(o.totalAmount) : "",
        totalAmountCurrency: o.totalAmountCurrency,
        approvalStatus: o.approvalStatus, orderStatus: o.orderStatus,
        etd: formatDate(o.estimatedTimeOfDeparture),
        finalPrice: o.finalPrice != null ? Number(o.finalPrice) : "",
        incoterm: o.incoterm, company: o.company,
      }));
      zebraAndBorder(orderWs);

      /* ── 4. Shipment Requests ── */
      const srWs = wb.addWorksheet("Shipment Requests");
      srWs.columns = [
        { header: "ID",          key: "id",        width: 10 },
        { header: "Type",        key: "type",       width: 14 },
        { header: "Qty",         key: "qty",        width: 14 },
        { header: "Qty Unit",    key: "qtyUnit",    width: 10 },
        { header: "POL",         key: "pol",        width: 16 },
        { header: "POD",         key: "pod",        width: 16 },
        { header: "Approval",    key: "approval",   width: 16 },
        { header: "Status",      key: "status",     width: 16 },
        { header: "ETD",         key: "etd",        width: 14 },
        { header: "PO Company",  key: "poCompany",  width: 20 },
        { header: "PO #",        key: "poNumber",   width: 16 },
        { header: "SO Company",  key: "soCompany",  width: 20 },
        { header: "SO #",        key: "soNumber",   width: 16 },
        { header: "Ship To",     key: "shipTo",     width: 24 },
        { header: "Vendor",      key: "vendor",     width: 24 },
        { header: "Customer",    key: "customer",   width: 24 },
      ];
      styleHeader(srWs, 16);
      (filtered.shipmentRequests ?? []).forEach((sr) => srWs.addRow({
        id: sr.id, type: sr.shipmentRequestType,
        qty: sr.shipmentQuantity != null ? Number(sr.shipmentQuantity) : "",
        qtyUnit: sr.shipmentQuantityUnit,
        pol: sr.portOfLoading, pod: sr.portOfDischarge,
        approval: sr.approvalStatus, status: sr.status,
        etd: formatDate(sr.shipmentEstimatedTimeOfDeparture),
        poCompany: sr.purchaseOrderCompany, poNumber: sr.purchaseOrderNumber,
        soCompany: sr.salesOrderCompany,    soNumber: sr.salesOrderNumber,
        shipTo: sr.shipmentRequestTo,
        vendor: sr.purchaseOrderStakeholderName, customer: sr.salesOrderStakeholderName,
      }));
      zebraAndBorder(srWs);

      /* ── 5. Shipping Instructions ── */
      const siWs = wb.addWorksheet("Shipping Instructions");
      siWs.columns = [
        { header: "ID",          key: "id",       width: 10 },
        { header: "SI #",        key: "siNum",    width: 20 },
        { header: "Type",        key: "type",      width: 14 },
        { header: "Qty",         key: "qty",       width: 14 },
        { header: "Qty Unit",    key: "qtyUnit",   width: 10 },
        { header: "Status",      key: "status",    width: 16 },
        { header: "ATD",         key: "atd",       width: 14 },
        { header: "ATA",         key: "ata",       width: 14 },
        { header: "BL #",        key: "bl",        width: 16 },
        { header: "POL",         key: "pol",       width: 16 },
        { header: "POD",         key: "pod",       width: 16 },
        { header: "Shipper",     key: "shipper",   width: 20 },
        { header: "Consignee",   key: "consignee", width: 20 },
        { header: "PO Vendor",   key: "vendor",    width: 20 },
        { header: "SO Customer", key: "customer",  width: 20 },
        { header: "Debit USD",   key: "debitUsd",  width: 14 },
        { header: "Credit USD",  key: "creditUsd", width: 14 },
      ];
      styleHeader(siWs, 18);
      (filtered.shippingInstructions ?? []).forEach((si) => siWs.addRow({
        id: si.id, siNum: si.finalShippingInstructionNumber ?? si.id,
        type: si.shippingInstructionType,
        qty: si.shipmentQuantity != null ? Number(si.shipmentQuantity) : "",
        qtyUnit: si.shipmentQuantityUnit, status: si.status,
        atd: formatDate(si.actualTimeOfDeparture), ata: formatDate(si.actualTimeOfArrival),
        pol: si.pointOfLoading, pod: si.pointOfUnloading,
        shipper: si.shipperName, consignee: si.consigneeName,
        vendor: si.purchaseOrderStakeholderName, customer: si.salesOrderStakeholderName,
        debitUsd:  si.debitNoteUsd  != null ? Number(si.debitNoteUsd)  : "",
        creditUsd: si.creditNoteUsd != null ? Number(si.creditNoteUsd) : "",
      }));
      zebraAndBorder(siWs);

      /* ── 6. Samples ── */
      const sampWs = wb.addWorksheet("Samples");
      sampWs.columns = [
        { header: "ID",       key: "id",       width: 10 },
        { header: "Role",     key: "role",      width: 10 },
        { header: "Grade",    key: "grade",     width: 18 },
        { header: "Origin",   key: "origin",    width: 14 },
        { header: "Crop",     key: "crop",      width: 10 },
        { header: "Delivery", key: "delivery",  width: 14 },
        { header: "Approved", key: "approved",  width: 14 },
        { header: "Purpose",  key: "purpose",   width: 16 },
        { header: "Sender",   key: "sender",    width: 20 },
        { header: "Receiver", key: "receiver",  width: 20 },
        { header: "AWB",      key: "awb",       width: 16 },
        { header: "Nicotine", key: "nicotine",  width: 12 },
        { header: "Sugar",    key: "sugar",     width: 12 },
        { header: "Remark",   key: "remark",    width: 30 },
      ];
      styleHeader(sampWs, 14);
      [
        ...(filtered.sentSamples     ?? []).map((s) => ({ ...s, role: "Sent"     })),
        ...(filtered.receivedSamples ?? []).map((s) => ({ ...s, role: "Received" })),
      ].forEach((s) => sampWs.addRow({
        id: s.id, role: s.role, grade: s.itemBuyingGrade, origin: s.salesOrigin,
        crop: s.salesCrop, delivery: formatDate(s.deliveryDate),
        approved: s.approved, purpose: s.purpose,
        sender: s.senderName, receiver: s.receiverName, awb: s.awbNumber,
        nicotine: s.salesNicotine != null ? Number(s.salesNicotine) : "",
        sugar:    s.salesSugar    != null ? Number(s.salesSugar)    : "",
        remark: s.remark,
      }));
      zebraAndBorder(sampWs);

      /* ── 7. Claims ── */
      const claimWs = wb.addWorksheet("Claims");
      claimWs.columns = [
        { header: "Note",        key: "note",     width: 16 },
        { header: "Type",        key: "type",      width: 18 },
        { header: "Date",        key: "date",      width: 14 },
        { header: "Amount",      key: "amount",    width: 14 },
        { header: "Currency",    key: "currency",  width: 10 },
        { header: "Unit USD",    key: "unitUsd",   width: 14 },
        { header: "Total USD",   key: "totalUsd",  width: 16 },
        { header: "SI ID",       key: "siId",      width: 10 },
        { header: "SI #",        key: "siNum",     width: 20 },
        { header: "ATD",         key: "atd",       width: 14 },
        { header: "PO Vendor",   key: "vendor",    width: 20 },
        { header: "SO Customer", key: "customer",  width: 20 },
        { header: "Remark",      key: "remark",    width: 30 },
      ];
      styleHeader(claimWs, 14);
      claims.forEach((c) => claimWs.addRow({
        note: c.direction === "DEBIT" ? "Debit (Profit)" : "Credit (Loss)",
        type: c.claimType ?? "",
        date: formatDate(c.date),
        amount: c.amount   != null ? Number(c.amount)   : "",
        currency: c.currency ?? "",
        unitUsd:  c.amountUsd != null ? Number(c.amountUsd) : "",
        totalUsd: c.totalUsd  != null ? Number(c.totalUsd)  : "",
        siId:  c.si?.id ?? "",
        siNum: c.si?.finalShippingInstructionNumber ?? c.si?.id ?? "",
        atd: formatDate(c.si?.actualTimeOfDeparture),
        vendor:   c.si?.purchaseOrderStakeholderName ?? "",
        customer: c.si?.salesOrderStakeholderName    ?? "",
        remark: c.remark ?? "",
      }));
      zebraAndBorder(claimWs);

      /* numeric formats */
      [[offerWs, [7,12,13]], [orderWs, [7,9,14]], [srWs, [3]], [siWs, [4,17,18]], [sampWs, [12,13]], [claimWs, [4,6,7]]]
        .forEach(([ws, cols]) => cols.forEach((c) => ws.getColumn(c).numFmt = "#,##0.00"));

      const buf   = await wb.xlsx.writeBuffer();
      const slug  = (sName).replace(/[^a-z0-9]/gi, "_").toLowerCase();
      const today = format(new Date(), "yyyyMMdd_HHmm");
      saveAs(
        new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `stakeholder_map_${slug}_${today}.xlsx`
      );
      toast.success("Exported successfully.");
    } catch (err) {
      console.error("Stakeholder Map export failed:", err);
      toast.error("Export failed.");
    }
  }, [filtered, allOffers, allOrders, claims, profile, dateFrom, dateTo]);

  /**
   * Chart id suffix — changes when the selected stakeholder changes so ApexCharts
   * destroys and recreates all instances cleanly on stakeholder switch.
   */
  const cid = selected?.id ?? "none";

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Stakeholder Map</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            360° activity profile — offers · orders · shipments · shipping instructions · samples · claims
          </Typography>
        </Box>
        {selected && (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export to Excel (filtered data — 7 sheets)">
              <span>
                <IconButton onClick={exportToExcel} disabled={loading || !filtered}
                  color="primary" size="large">
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reload profile">
              <IconButton onClick={() => loadProfile(selected.id)} disabled={loading}
                color="primary" size="large">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {/* ── Stakeholder Picker ───────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Select a stakeholder to profile</Typography>
        <Autocomplete
          options={stakeholders}
          value={selected}
          onChange={handleSelect}
          getOptionLabel={(o) => o ? `${o.name ?? ""}${o.code ? ` (${o.code})` : ""} — ${o.type ?? ""}` : ""}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          groupBy={(o) => o.type ?? "Other"}
          renderInput={(params) => <TextField {...params} label="Search by name or code…" size="small" fullWidth />}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id}>
              <Stack>
                <Typography variant="body2">{o.name}</Typography>
                <Typography variant="caption" color="text.secondary">{o.code ? `${o.code} · ` : ""}{o.type}</Typography>
              </Stack>
            </Box>
          )}
          sx={{ maxWidth: 500 }}
        />
      </Paper>

      {/* ── Loading state ────────────────────────────────────────────────── */}
      {loading && (
        <Box textAlign="center" py={6}>
          <Typography color="text.secondary">Loading profile…</Typography>
        </Box>
      )}

      {/* ── Full Profile (rendered only when a profile is loaded) ─────────── */}
      {!loading && profile && (
        <>
          {/* 1. Profile Card */}
          <ProfileCard stakeholder={profile.stakeholder} />

          {/* 2. Trade Pipeline Banner — uses filtered counts */}
          <TradePipeline profile={filtered} claims={claims} />

          {/* 3. Date Filter Bar */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={700}>Filter by date</Typography>
              <TextField label="From" type="date" size="small"
                InputLabelProps={{ shrink: true }} value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                sx={{ width: 160 }} />
              <TextField label="To" type="date" size="small"
                InputLabelProps={{ shrink: true }} value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                sx={{ width: 160 }} />
              {(dateFrom || dateTo) && (
                <Tooltip title="Clear date filter">
                  <IconButton size="small" color="warning"
                    onClick={() => { setDateFrom(""); setDateTo(""); }}>
                    <FilterAltOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto !important" }}>
                {(dateFrom || dateTo) ? (
                  <>
                    Filtered: <strong>{allOffers.length}</strong> offers ·{" "}
                    <strong>{allOrders.length}</strong> orders ·{" "}
                    <strong>{(filtered?.shippingInstructions ?? []).length}</strong> SIs ·{" "}
                    <strong>{claims.length}</strong> claims
                  </>
                ) : "All dates shown — set a range to filter"}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              Offers: offer date · Orders: order date · Shipment Requests: creation date · Shipping Instructions: SI date · Claims: note date · Samples: delivery date
            </Typography>
          </Paper>

          {/* 4. Stat Cards */}
          <Grid container spacing={2} mb={3}>
            {[
              { label: "Customer Offers",      value: filtered?.customerOffers?.length  ?? 0, color: theme.palette.primary.main,  sub: `${filtered?.vendorOffers?.length ?? 0} vendor offer(s)` },
              { label: "Customer Orders",       value: filtered?.customerOrders?.length  ?? 0, color: theme.palette.info.main,     sub: `${filtered?.vendorOrders?.length ?? 0} vendor order(s)` },
              { label: "Shipment Requests",     value: filtered?.shipmentRequests?.length ?? 0, color: theme.palette.warning.main,  sub: `leading to ${filtered?.shippingInstructions?.length ?? 0} SI(s)` },
              { label: "Shipping Instructions", value: filtered?.shippingInstructions?.length ?? 0, color: "#7b5ea7",              sub: `with ${claims.length} claim note(s)` },
              { label: "Samples",               value: allSamples.length,              color: theme.palette.success.main,  sub: `${filtered?.sentSamples?.length ?? 0} sent · ${filtered?.receivedSamples?.length ?? 0} recv` },
              { label: "Claims",                value: claims.length,                  color: theme.palette.error.main,    sub: `${claims.filter((c) => c.direction === "DEBIT").length} debit · ${claims.filter((c) => c.direction === "CREDIT").length} credit` },
            ].map((card) => (
              <Grid item xs={6} sm={4} md={2} key={card.label}>
                <StatCard {...card} />
              </Grid>
            ))}
          </Grid>

          {/* ── Charts Row 1: status distributions ───────────────────────── */}
          <Grid container spacing={2} mb={2}>
            {/* Orders by status */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>Orders by Status</Typography>
                <ChartOrEmpty hasData={orderStatusChart.labels.length > 0}>
                  <PieChart key={`os-${cid}`} labels={orderStatusChart.labels} series={orderStatusChart.series}
                    donut showTotal totalLabel="Orders" height={250} chartId={`sm-os-${cid}`} />
                </ChartOrEmpty>
              </Paper>
            </Grid>
            {/* Offers by approval */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>Offers by Approval</Typography>
                <ChartOrEmpty hasData={offerApprovalChart.labels.length > 0}>
                  <PieChart key={`oa-${cid}`} labels={offerApprovalChart.labels} series={offerApprovalChart.series}
                    donut showTotal totalLabel="Offers" height={250} chartId={`sm-oa-${cid}`} />
                </ChartOrEmpty>
              </Paper>
            </Grid>
            {/* Shipping instructions by status */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                  Shipping Instructions by Status
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {(filtered?.shippingInstructions ?? []).length} total · {claims.length} carry claim notes
                </Typography>
                <ChartOrEmpty hasData={siStatusChart.labels.length > 0}>
                  <PieChart key={`si-${cid}`} labels={siStatusChart.labels} series={siStatusChart.series}
                    donut showTotal totalLabel="SIs" height={250} chartId={`sm-si-${cid}`} />
                </ChartOrEmpty>
              </Paper>
            </Grid>
          </Grid>

          {/* ── Charts Row 2: time-series ────────────────────────────────── */}
          <Grid container spacing={2} mb={3}>
            {/* Shipment volume area chart (from SIs) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                  Shipment Volume Over Time
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Monthly kg shipped · source: Shipping Instructions
                </Typography>
                <ShipmentVolumeTrendChart
                  shippingInstructions={filtered?.shippingInstructions ?? []}
                  chartId={`sm-vol-${cid}`}
                />
              </Paper>
            </Grid>

            {/* Claims P&L grouped bar chart (derived from SIs) */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                  Claims P&amp;L by Month
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Debit (profit) vs Credit (loss) in USD · source: claim note dates on SIs
                </Typography>
                <ClaimsPnlChart claims={claims} chartId={`sm-pnl-${cid}`} />
              </Paper>
            </Grid>

            {/* Samples by year */}
            <Grid item xs={12} md={3}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>Samples by Year</Typography>
                <ChartOrEmpty hasData={sampleBarChart.categories.length > 0} height={250}>
                  <ReactApexChart
                    key={`sm-samp-${cid}-${sampleBarChart.categories.join("")}`}
                    type="bar"
                    height={250}
                    options={{
                      chart: { type: "bar", toolbar: { show: false }, id: `sm-samp-${cid}`, destroyBeforeMount: true, animations: { enabled: false } },
                      theme: { mode: theme.palette.mode },
                      colors: ["#425078", "#d7e7fc"],
                      plotOptions: { bar: { borderRadius: 3, columnWidth: "60%", horizontal: false } },
                      dataLabels: { enabled: false },
                      xaxis: { categories: sampleBarChart.categories, labels: { style: { fontSize: "11px" } } },
                      yaxis: { labels: { style: { fontSize: "11px" } } },
                      legend: { position: "top", fontSize: "12px" },
                      grid: { borderColor: theme.palette.divider },
                    }}
                    series={sampleBarChart.series}
                  />
                </ChartOrEmpty>
              </Paper>
            </Grid>
          </Grid>

          {/* ── Tabbed Detail Tables ─────────────────────────────────────── */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper",
                    "& .MuiTab-root": { fontWeight: 600, fontSize: 13 } }}>
              {tabs.map((label, i) => <Tab key={i} label={label} />)}
            </Tabs>
            <Box sx={{ p: 2 }}>
              {activeTab === 0 && <OffersTab offers={allOffers} />}
              {activeTab === 1 && <OrdersTab orders={allOrders} />}
              {activeTab === 2 && <ShipmentsTab shipmentRequests={filtered?.shipmentRequests ?? []} />}
              {activeTab === 3 && <ShippingInstructionsTab shippingInstructions={filtered?.shippingInstructions ?? []} />}
              {activeTab === 4 && <SamplesTab sentSamples={filtered?.sentSamples ?? []} receivedSamples={filtered?.receivedSamples ?? []} />}
              {activeTab === 5 && <ClaimsTab claims={claims} />}
            </Box>
          </Paper>
        </>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {!loading && !profile && (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", borderRadius: 2,
          border: "1px dashed", borderColor: "divider" }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 56, color: "text.disabled", mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            Select a stakeholder above to see their full activity profile
          </Typography>
          <Typography variant="body2" color="text.disabled" mt={0.5}>
            Offers · Orders · Shipment Requests · Shipping Instructions · Samples · Claims
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
